import { useState, useEffect, useRef } from 'react';
import { NavBar, Logo, SearchBar, SearchResultStats } from './navBar';
import StarRating from './StarRating';
import { useMovies } from './CustomHooks/useMovies';
import { useLocalStorageState } from './CustomHooks/useLocalStorageState';
import { useKey } from './CustomHooks/useKey';

const average = (arr) => arr.reduce((acc, cur, i, arr) => acc + cur / arr.length, 0);
const apiKey = 'c66617b7';

export default function App() {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const { movies, isLoading, error } = useMovies(query, handleCloseMovie);
  const [watched, setWatched] = useLocalStorageState([], 'watchedMovies');

  function onSelectMovie(movieId) {
    setSelectedId((currentMovieId) => (currentMovieId === movieId ? null : movieId));
  }

  function handleAddWatched(movie) {
    const newMovie = {
      imdbID: movie.imdbID,
      Title: movie.Title,
      Year: movie.Year,
      Poster: movie.Poster,
      runtime: +movie.Runtime.split(' ')[0],
      imdbRating: movie.imdbRating,
      userRating: movie.userRating,
      countRatingDecisions: movie.countRatingDecisions,
    };

    const movieExists = watched.find((watchedMovie) => watchedMovie.imdbID === newMovie.imdbID);

    !movieExists && setWatched((currentWatchedMovies) => [...currentWatchedMovies, newMovie]);

    setSelectedId('');
  }

  function handleDeleteWatched(movieImdbId) {
    setWatched((currentWatchMovie) => currentWatchMovie.filter((currentWatchMovie) => currentWatchMovie.imdbID !== movieImdbId));
  }

  function handleCloseMovie() {
    setSelectedId(null);
  }

  return (
    <>
      <NavBar>
        <Logo />
        <SearchBar query={query} setQuery={setQuery} />
        <SearchResultStats movies={movies} />
      </NavBar>
      <Main>
        <Box>
          {isLoading && <Loader />}
          {!isLoading && !error && <MovieList movies={movies} onSelectMovie={onSelectMovie} />}
          {error && <ErrorMessage errorMessage={error} />}
        </Box>

        <Box>
          <>
            {selectedId && <SelectedMovie selectedId={selectedId} watchedMovies={watched} onCloseMovie={() => setSelectedId(null)} handleAddWatched={handleAddWatched} />}
            {!selectedId && (
              <>
                <WatchedSummary watched={watched} />
                <WatchedMovies watched={watched} onDeleteWatched={handleDeleteWatched} />
              </>
            )}
          </>
        </Box>
      </Main>
    </>
  );
}

function Loader() {
  return <p className="loader">Loading..</p>;
}

function ErrorMessage({ errorMessage }) {
  return (
    <p className="error">
      <span>❌ {errorMessage}</span>
    </p>
  );
}

function Main({ children }) {
  return <main className="main">{children}</main>;
}

function Box({ children }) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="box">
      <ExpandButton isOpen={isOpen} setIsOpen={setIsOpen} />
      {isOpen && children}
    </div>
  );
}

function ExpandButton({ isOpen, setIsOpen }) {
  return (
    <button className="btn-toggle" onClick={() => setIsOpen((open) => !open)}>
      {isOpen ? '–' : '+'}
    </button>
  );
}

function MovieList({ movies, onSelectMovie }) {
  return (
    <ul className="list list-movies">
      {movies?.map((movie) => (
        <Movie movie={movie} key={movie.imdbID} onSelectMovie={onSelectMovie} />
      ))}
    </ul>
  );
}

function Movie({ movie, onSelectMovie }) {
  return (
    <li onClick={() => onSelectMovie(movie.imdbID)}>
      <img src={movie.Poster} alt={`${movie.Title} poster`} />
      <h3>{movie.Title}</h3>
      <div>
        <p>
          <span>🗓</span>
          <span>{movie.Year}</span>
        </p>
      </div>
    </li>
  );
}

function SelectedMovie({ selectedId, watchedMovies, onCloseMovie, handleAddWatched }) {
  const [movie, setMovie] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [userRating, setUserRating] = useState(null);
  const isWatched = watchedMovies.map((movie) => movie.imdbID).includes(selectedId);
  const watchedUserRating = watchedMovies.find((watchedMovie) => watchedMovie.imdbID === selectedId)?.userRating;
  const countRef = useRef(0);

  const { Title: title, Poster: poster, Runtime: runtime, imdbRating, Plot: plot, Released: releaseDate, Actors: actors, Director: director, Genre: genre } = movie;

  useEffect(() => {
    if (!userRating) return;

    countRef.current += 1;
  }, [userRating]);

  useKey('Escape', onCloseMovie);

  useEffect(
    function () {
      async function getMovieDetails() {
        try {
          setIsLoading(true);
          const res = await fetch(` http://www.omdbapi.com/?apikey=${apiKey}&&i=${selectedId}`);
          const data = await res.json();

          if (!res.ok) throw new Error('There was an error trying to fetch the movie.');

          setMovie(data);
        } catch (err) {
          console.error(err.message);
        } finally {
          setIsLoading(false);
        }
      }
      getMovieDetails();
    },
    [selectedId]
  );

  useEffect(
    function () {
      if (!title) return;
      document.title = `Movie | ${title}`;

      return function () {
        document.title = 'Use Popcorn';
      };
    },
    [title]
  );

  function handleAdd() {
    handleAddWatched({ ...movie, userRating: userRating, countRatingDecisions: countRef.current });
  }

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="details">
          <header>
            <button className="btn-back" onClick={onCloseMovie}>
              &larr;
            </button>
            <img src={poster} alt={`Poster of ${title} movie`} />
            <div className="details-overview">
              <h2>{title}</h2>
              <p>
                {releaseDate} &bull; {runtime}
              </p>
              <p>{genre}</p>
              <p>
                <span>⭐️</span>
                {imdbRating} IMDb rating
              </p>
            </div>
          </header>

          <section>
            <div className="rating">
              {!isWatched && (
                <>
                  <StarRating maxRating={10} size="24px" onSetRating={setUserRating} />

                  {userRating && (
                    <button className="btn-add" onClick={handleAdd}>
                      Add to list
                    </button>
                  )}
                </>
              )}
              {isWatched && <p>You rated this {watchedUserRating}/10 !</p>}
            </div>
            <p>
              <em>{plot}</em>
            </p>
            <p>Starring: {actors}</p>
            <p>Directed by {director}</p>
          </section>
        </div>
      )}
    </>
  );
}

function WatchedSummary({ watched }) {
  const avgImdbRating = average(watched.map((movie) => movie.imdbRating));
  const avgUserRating = average(watched.map((movie) => movie.userRating));
  const avgRuntime = average(watched.map((movie) => movie.runtime));

  return (
    <div className="summary">
      <h2>Movies you watched</h2>
      <div>
        <p>
          <span>#️⃣</span>
          <span>{watched.length} movies</span>
        </p>
        <p>
          <span>⭐️</span>
          <span>{avgImdbRating.toFixed(2)}</span>
        </p>
        <p>
          <span>🌟</span>
          <span>{avgUserRating.toFixed(2)}</span>
        </p>
        <p>
          <span>⏳</span>
          <span>{avgRuntime} min</span>
        </p>
      </div>
    </div>
  );
}

function WatchedMovies({ watched, onDeleteWatched }) {
  return (
    <ul className="list">
      {watched.map((movie, index) => (
        <WatchedMovie key={index} movie={movie} onDeleteWatched={onDeleteWatched} />
      ))}
    </ul>
  );
}

function WatchedMovie({ movie, onDeleteWatched }) {
  return (
    <li key={movie.imdbID}>
      <img src={movie.Poster} alt={`${movie.Title} poster`} />
      <h3>{movie.Title}</h3>
      <div>
        <p>
          <span>⭐️</span>
          <span>{movie.imdbRating}</span>
        </p>
        <p>
          <span>🌟</span>
          <span>{movie.userRating}</span>
        </p>
        <p>
          <span>⏳</span>
          <span>{movie.runtime} min</span>
        </p>

        <button className="btn-delete" onClick={() => onDeleteWatched(movie.imdbID)}>
          X
        </button>
      </div>
    </li>
  );
}
